/**
 * CypAdhdMedicalScreeningSummary.js
 *
 * Read-only read-back of WLC's treatment contraindication screening
 * (cypAdhdFlow.medicalScreening === 'contraindicationsV1'). Shown in two
 * places that never edit the answers:
 *   - the MDT Review (20_cypAdhdDD/0_DDReview.js), so the diagnostic
 *     decision meeting sees what the interviewing clinician recorded;
 *   - the PAD Health Checks page (23_cypAdhdPAD/4_HLT.js), under the height
 *     and weight readings and above the contraindication decision, so the
 *     prescriber sees the screen next to the readings taken that day.
 *
 * Chrome is the "not in the report" card used for Emerging Formulation and
 * Confidential Notes (amber fill, amber border, and the corner line "Not
 * included in final report"). These answers are a reference for medication
 * planning. They are not AI inputs and they are not written into the patient
 * report, and the card has to say so: a plum table next to the report
 * sections was being read as content that would be drafted (Oct 2026).
 * The tick and cross stay the shared DecisionIcon, so a recorded Yes still
 * looks the same as on the SSI tables.
 *
 * Props:
 *   groups      the config groups to show (one or both). A group label row
 *               separates them when more than one is passed.
 *   data        the stored medicalScreening object (or anything; it is
 *               normalised, so absent/malformed renders as all unrecorded).
 *   title       heading for the card.
 *   sourceNote  footer sentence saying where the answers came from.
 *
 * A row that was never answered reads "Not recorded" rather than being
 * hidden or shown as No, and the count line under the title names how many
 * are still unrecorded, so an unfinished screen is never read as clear.
 * Nothing here computes a conclusion (regulatory boundary).
 */
import React from 'react';
import { DecisionIcon } from '../../22_cypAdhdSSI/components/CypAdhdSsiDecisionTables';
import {
  normalizeMedicalScreeningData,
  medicalScreeningCountLine,
} from '../config/cypAdhdMedicalScreeningConfig';

export default function CypAdhdMedicalScreeningSummary({
  groups,
  data,
  title,
  sourceNote,
}) {
  const items = normalizeMedicalScreeningData(data).items;
  const shownGroups = groups || [];
  const showGroupLabels = shownGroups.length > 1;

  return (
    <section
      className="bg-amber-50 rounded-2xl border border-amber-200 text-left px-6 pt-6 pb-2 shadow-md flex flex-col"
      data-testid="medical-screening-summary"
    >
      {title && <h4 className="font-bold text-lg mb-1">{title}</h4>}
      <p className="text-sm text-gray-600 mb-3">
        For reference when planning medication. These answers are not written into the patient report.
      </p>
      <p className="text-sm text-gray-600 mb-3" data-testid="medical-screening-count">
        {medicalScreeningCountLine(shownGroups, items)}
      </p>

      <table
        className="w-full text-left rounded-lg overflow-hidden border border-amber-200"
        style={{ tableLayout: 'fixed' }}
      >
        <caption className="sr-only">
          Treatment contraindication screening recorded during the Clinical Interview
        </caption>
        <colgroup>
          <col style={{ width: '48%' }} />
          <col style={{ width: '14%' }} />
          <col style={{ width: '38%' }} />
        </colgroup>
        <thead>
          <tr className="bg-amber-100 text-amber-950">
            <th scope="col" className="p-3 text-xs">Item</th>
            <th scope="col" className="p-3 text-center text-xs">Decision</th>
            <th scope="col" className="p-3 text-xs">Detail</th>
          </tr>
        </thead>
        <tbody>
          {shownGroups.map((group) => (
            <React.Fragment key={group.id}>
              {showGroupLabels && (
                <tr className="bg-amber-100/80 font-bold text-amber-950 text-sm">
                  <th scope="rowgroup" colSpan={3} className="p-2 text-left">
                    {group.title}
                  </th>
                </tr>
              )}
              {group.items.map((item) => {
                const row = items[item.id] || {};
                const isYes = row.present === true;
                const detail = isYes && typeof row.detail === 'string' ? row.detail.trim() : '';
                return (
                  <tr key={item.id} className="bg-white border-b border-amber-100">
                    <td className="p-3 text-sm">{item.label}</td>
                    <td className="p-3 text-center">
                      <DecisionIcon value={row.present} />
                    </td>
                    {/* Detail only ever belongs to a Yes. A No or unrecorded
                        row shows an em dash so the column never looks like
                        a missing value. */}
                    <td className="p-3 text-sm text-gray-800 whitespace-pre-wrap break-words">
                      {detail || <span className="text-gray-400" aria-label="No detail">—</span>}
                    </td>
                  </tr>
                );
              })}
            </React.Fragment>
          ))}
        </tbody>
      </table>

      {sourceNote && (
        <p className="text-xs text-gray-500 mt-2">{sourceNote}</p>
      )}
      <p className="text-xs italic text-amber-900/80 text-right mt-1 mb-0">
        Not included in final report
      </p>
    </section>
  );
}
